import React from "react";
import {
  ArrowRight,
  Check,
  Code,
  Globe,
  MessageSquare,
  Palette,
  Smartphone,
} from "lucide-react";
import Header from "./Header";
import "./Home.css";
import { services, process, techStack, whyChooseUs } from "../mock";
import Reveal from "./Reveal";
import TechLogo from "./TechLogo";

const Home = () => {
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  const asset = (path) => `${process.env.PUBLIC_URL || ""}${path}`;
  const logoSrc = asset("/images/grayman.jpeg");
  const heroTechWebpSrc = asset("/images/tech.webp");
  const heroTechPngSrc = asset("/images/tech.png");

  const featured = [
    {
      title: "Web apps & websites",
      description:
        "Fast, responsive sites and web apps built with modern stacks and clean UI.",
      image: asset("/images/featured-web-light.svg"),
    },
    {
      title: "Mobile app development",
      description:
        "iOS & Android experiences with smooth UX, reliable performance, and polish.",
      image: asset("/images/featured-mobile-light.svg"),
    },
    {
      title: "Product design & UX",
      description:
        "Wireframes to high-fidelity UI, design systems, and UX improvements that convert.",
      image: asset("/images/featured-design-light.svg"),
    },
  ];

  const ScrollDoodle = () => (
    <svg
      className="hero-scroll-arrow"
      viewBox="0 0 140 120"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M74 10c0 28-18 38-18 56 0 12 10 20 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M78 88c-10 0-18-4-28-12"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path
        d="M78 88c-2-10 1-20 10-30"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );

  const solutionHighlights = [
    {
      title: "Leverage Technology",
      description:
        "Modern stacks and clean systems that keep your product fast and reliable.",
      icon: <Globe size={26} />,
    },
    {
      title: "Boost Performance",
      description:
        "Polished UI and measurable performance improvements that users feel.",
      icon: <Code size={26} />,
    },
    {
      title: "Vetted & Secure",
      description:
        "Best practices, reviews, and secure defaults from day one to launch.",
      icon: <Check size={26} />,
    },
    {
      title: "Business Success",
      description:
        "Ship outcomes: conversion-focused UX, scalable engineering, and support.",
      icon: <MessageSquare size={26} />,
    },
  ];

  const serviceIcons = {
    "Web Application Development": <Globe size={28} />,
    "Mobile App Development": <Smartphone size={28} />,
    "Website Design & Development": <Palette size={28} />,
    "Custom Software Solutions": <Code size={28} />,
    "Tech Consulting & Architecture": <MessageSquare size={28} />,
    "Product Design & UX": <Palette size={28} />,
  };

  return (
    <div className="home">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-container">
          <div className="hero-content">

            <Reveal delayMs={80}>
              <h1 className="display-huge hero-title">
                We build digital products that{" "}
                <span className="accent-text">feel premium</span> and{" "}
                <span className="accent-text">scale fast</span>.
              </h1>
            </Reveal>

            <Reveal delayMs={140}>
              <p className="body-large hero-description">
                Strategy, design, and engineering for startups and teams that
                care about polish. From MVP to growth: we ship clean UI, smooth
                UX, and maintainable code.
              </p>
            </Reveal>

            <Reveal className="hero-actions" delayMs={220}>
              <button onClick={scrollToContact} className="btn-primary">
                Schedule a call <ArrowRight size={18} />
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("services")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-secondary"
              >
                Explore services
              </button>

              <span
                className="hero-scroll-doodle hero-scroll-doodle--inline"
                role="button"
                tabIndex={0}
                aria-label="Scroll down"
                onClick={() =>
                  document
                    .getElementById("solutions")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document
                      .getElementById("solutions")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                <ScrollDoodle />
              </span>
            </Reveal>
          </div>

          <Reveal className="hero-visual" delayMs={140}>
            <div className="hero-right-visual">
              <picture>
                <source srcSet={heroTechWebpSrc} type="image/webp" />
                <img
                  className="hero-tech-image"
                  src={heroTechPngSrc}
                  alt="Technology illustration"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Solutions row (like the reference) */}
      <section id="solutions" className="solutions-section">
        <div className="section-container solutions-container">
          <Reveal className="section-header">
            <h2 className="display-medium">All solutions in one platform</h2>
          </Reveal>

          <div className="solutions-grid">
            {solutionHighlights.map((item, idx) => (
              <Reveal
                key={item.title}
                className="solution-card"
                delayMs={70 + idx * 60}
              >
                <div className="solution-icon">{item.icon}</div>
                <h3 className="heading-3">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
                <button
                  className="solution-link"
                  onClick={() =>
                    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Learn more <ArrowRight size={16} />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured visuals */}
      <section id="work" className="featured-section">
        <div className="section-container">
          <Reveal className="section-header">
            <h2 className="display-large">Work that ships</h2>
            <p className="body-medium section-subtitle">
              A snapshot of the outcomes we deliver — clean UI, smooth UX, and
              production-ready engineering.
            </p>
          </Reveal>

          <div className="featured-grid">
            {featured.map((item, idx) => (
              <Reveal
                key={item.title}
                className="featured-card"
                delayMs={80 + idx * 70}
              >
                <div className="featured-media">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="featured-image"
                  />
                </div>
                <div className="featured-content">
                  <h3 className="heading-2">{item.title}</h3>
                  <p className="body-medium">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <Reveal className="section-header">
            <h2 className="display-large">Services</h2>
            <p className="body-medium section-subtitle">
              Clean execution across strategy, design, and engineering.
            </p>
          </Reveal>
          <div className="services-grid">
            {services.map((service, idx) => (
              <Reveal
                key={service.id}
                className="service-card"
                delayMs={80 + idx * 60}
              >
                <div className="service-icon">{serviceIcons[service.title]}</div>
                <h3 className="heading-2">{service.title}</h3>
                <p className="body-medium service-description">
                  {service.description}
                </p>
                <ul className="service-features">
                  {service.features.map((feature, featureIdx) => (
                    <li key={featureIdx} className="body-small">
                      <Check size={16} className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="about" className="why-section">
        <div className="section-container">
          <Reveal className="section-header">
            <h2 className="display-large">Why GrayMan</h2>
            <p className="body-medium section-subtitle">
              A small team with senior execution and an obsession with polish.
            </p>
          </Reveal>
          <div className="why-grid">
            {whyChooseUs.map((item, idx) => (
              <Reveal key={idx} className="why-card" delayMs={80 + idx * 50}>
                <h3 className="heading-3">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process-section">
        <div className="section-container">
          <Reveal className="section-header">
            <h2 className="display-large">Process</h2>
            <p className="body-medium section-subtitle">
              A simple flow that keeps quality high and timelines predictable.
            </p>
          </Reveal>
          <div className="process-timeline">
            {process.map((step, idx) => (
              <Reveal key={idx} className="process-step" delayMs={80 + idx * 60}>
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="heading-2">{step.title}</h3>
                  <p className="body-medium">{step.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section id="tech-stack" className="tech-section">
        <div className="section-container">
          <Reveal className="section-header">
            <h2 className="display-large">Tech stack</h2>
            <p className="body-medium section-subtitle">
              Modern, scalable, and proven in production.
            </p>
          </Reveal>
          <div className="tech-grid">
            {techStack.map((stack, idx) => (
              <Reveal
                key={idx}
                className="tech-category"
                delayMs={80 + idx * 70}
              >
                <h3 className="heading-3">{stack.category}</h3>
                <div className="tech-list">
                  {stack.technologies.map((tech, techIdx) => (
                    <span
                      key={techIdx}
                      className="tech-item body-medium"
                      style={{
                        "--float-delay": `${(techIdx % 10) * 110}ms`,
                        "--float-duration": `${3400 + (techIdx % 7) * 260}ms`,
                      }}
                    >
                      <TechLogo name={tech} />
                      <span className="tech-item-label">{tech}</span>
                    </span>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-content">
            <Reveal className="contact-text">
              <h2 className="display-large">Let’s build something great.</h2>
              <p className="body-large">
                Book a free 30‑minute call. We’ll align on scope, timeline, and
                the fastest path to a premium result.
              </p>
              <div className="contact-features">
                <div className="contact-feature">
                  <Check size={20} className="check-icon" />
                  <span className="body-medium">Free strategy call</span>
                </div>
                <div className="contact-feature">
                  <Check size={20} className="check-icon" />
                  <span className="body-medium">No commitment required</span>
                </div>
                <div className="contact-feature">
                  <Check size={20} className="check-icon" />
                  <span className="body-medium">Actionable next steps</span>
                </div>
              </div>
            </Reveal>
            <Reveal className="calendly-container" delayMs={120}>
              <iframe
                src="https://calendly.com/graymantechandconsultancy/30min"
                width="100%"
                height="700px"
                frameBorder="0"
                title="Schedule a Meeting"
              ></iframe>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <img 
                  src={logoSrc}
                  alt="GrayMan Tech" 
                  className="footer-logo-image"
                />
              </div>
              <p className="body-medium footer-tagline">
                Building digital products that scale
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="heading-3">Services</h4>
                <ul>
                  <li><button className="footer-link">Web Development</button></li>
                  <li><button className="footer-link">Mobile Apps</button></li>
                  <li><button className="footer-link">Consulting</button></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4 className="heading-3">Company</h4>
                <ul>
                  <li><button className="footer-link">About Us</button></li>
                  <li><button className="footer-link">Portfolio</button></li>
                  <li><button className="footer-link">Contact</button></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="body-small">© 2025 GrayMan Tech & Consultancy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;